import React from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { APP_SHELL_ACTION_ENUM } from "../../common/reducers/appShell";
import { useSelector } from "../../common/hooks/useSelector";
import SpecsRevisionService, {
  extractSpecsValidationResult,
  type SpecType,
} from "../../service/SpecsRevisionService";
import { loadSpecsEditorImportSpecs } from "./specsEditorUtils";
import type { SpecsCatalog } from "./specsModel/catalog";
import type {
  SpecsEditorValidationResult,
  SpecsValidationDialogApi,
} from "./SpecsValidationDialog";
import {
  createDefaultSpecsEntitySnapshot,
  createSpecsModelFromCatalog,
  getSpecsEntityId,
  isSpecsEntityIdAvailable,
  type SpecsEntityModel,
  type SpecsEntityType,
  restoreSpecsModel,
  upsertSpecsSnapshotIntoCatalog,
} from "./specsModel/specsLib";
import { validateSpecsEntityModel } from "./specsValidationAdapter";
import { SpecsPageView } from "./types";

export type SpecOption<Entity> = {
  id: string;
  label: string;
  entity: Entity;
};

type UseSpecStateConfig<Entity> = {
  specType: SpecType;
  getOptions: (catalog: SpecsCatalog | null) => SpecOption<Entity>[];
  parseDraft: (draftJson: string) => { specName: string; entity: Entity };
  modals: SpecsValidationDialogApi;
};

export type CreateSpecDraftInput = {
  specName: string;
  displayName?: string;
};

function getSpecTypeLabel(specType: SpecType) {
  if (specType === "family") return "family";
  if (specType === "fabric") return "fabric";
  if (specType === "decoration") return "decoration";
  if (specType === "product") return "product";
  return specType;
}

function formatJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function getLatestRevisionId(response: {
  data?: {
    revisions?: Array<{ revisionId?: string | null }>;
    currentDraftRevisionId?: string | null;
    currentPublishedRevisionId?: string | null;
  };
}) {
  return (
    response.data?.revisions?.[0]?.revisionId ??
    response.data?.currentDraftRevisionId ??
    response.data?.currentPublishedRevisionId ??
    null
  );
}

export function useSpecState<Entity>({
  specType,
  getOptions,
  parseDraft,
  modals,
}: UseSpecStateConfig<Entity>) {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.userInfo) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = React.useState<SpecsCatalog | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [draftJson, setDraftJson] = React.useState("");
  const [draftModel, setDraftModel] = React.useState<SpecsEntityModel | null>(
    null
  );
  const [draftModelRevision, setDraftModelRevision] = React.useState(0);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [publishLoading, setPublishLoading] = React.useState(false);
  const [loadingRevisionId, setLoadingRevisionId] = React.useState<
    string | null
  >(null);
  const hydratedDraftSpecNameRef = React.useRef<string | null>(null);
  const specTypeLabel = getSpecTypeLabel(specType);
  const loadErrorMessage = `Failed to load ${specTypeLabel} specs catalog`;
  const invalidJsonValidator = `${specType}-editor`;
  const invalidJsonMessage = "JSON parsing failed";
  const saveChangeNote = `Saved from ${specTypeLabel} JSON editor`;
  const publishChangeNote = `Saved and published from ${specTypeLabel} JSON editor`;

  React.useEffect(() => {
    dispatch({
      type: APP_SHELL_ACTION_ENUM.SET_SHOW_LEFT_NAVIGATION,
      payload: { showLeftNavigation: false },
      error: null,
    });

    return () => {
      dispatch({
        type: APP_SHELL_ACTION_ENUM.SET_SHOW_LEFT_NAVIGATION,
        payload: { showLeftNavigation: true },
        error: null,
      });
    };
  }, [dispatch]);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const nextCatalog =
          (await loadSpecsEditorImportSpecs()) as SpecsCatalog;
        if (!cancelled) {
          setCatalog(nextCatalog);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(
            nextError instanceof Error ? nextError.message : loadErrorMessage
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [loadErrorMessage]);

  const updateSearchParams = React.useCallback(
    (patch: { spec?: string; view?: SpecsPageView }) => {
      const nextParams = new URLSearchParams(searchParams);
      if (patch.spec !== undefined) {
        if (patch.spec) {
          nextParams.set("spec", patch.spec);
        } else {
          nextParams.delete("spec");
        }
      }
      if (patch.view !== undefined) {
        if (patch.view === SpecsPageView.History) {
          nextParams.set("view", SpecsPageView.History);
        } else {
          nextParams.delete("view");
        }
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const options = React.useMemo(
    () => getOptions(catalog),
    [catalog, getOptions]
  );
  const requestedSpecName = searchParams.get("spec") || "";
  const selectedOption =
    options.find((option) => option.id === requestedSpecName) ??
    options[0] ??
    null;
  const selectedSpecName = selectedOption?.id ?? "";
  const currentView: SpecsPageView =
    searchParams.get("view") === SpecsPageView.History
      ? SpecsPageView.History
      : SpecsPageView.Editor;

  React.useEffect(() => {
    modals.showValidationResult({ valid: true, issues: [] });
  }, [modals.showValidationResult, selectedSpecName]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (!selectedOption) {
      if (requestedSpecName) {
        updateSearchParams({ spec: "" });
      }
      return;
    }

    if (selectedOption.id !== requestedSpecName) {
      updateSearchParams({ spec: selectedOption.id });
    }
  }, [loading, requestedSpecName, selectedOption, updateSearchParams]);

  React.useEffect(() => {
    if (!draftModel) {
      setDraftModelRevision(0);
      return;
    }

    setDraftModelRevision(draftModel.getRevision());
    setDraftJson(formatJson(draftModel.save()));

    return draftModel.subscribe(() => {
      setDraftModelRevision(draftModel.getRevision());
      setDraftJson(formatJson(draftModel.save()));
    });
  }, [draftModel]);

  React.useEffect(() => {
    const debugState = {
      model: draftModel ?? undefined,
      get snapshot() {
        return this.model?.save();
      },
    };

    window.specs = debugState as any;

    return () => {
      delete window.specs;
    };
  }, [draftModel]);

  const hydrateDraftFromSnapshot = React.useCallback(
    (snapshot: Record<string, unknown>, specName: string) => {
      setDraftModel(restoreSpecsModel(specType as SpecsEntityType, snapshot));
      setDraftJson(formatJson(snapshot));
      hydratedDraftSpecNameRef.current = specName;
      updateSearchParams({ spec: specName });
    },
    [specType, updateSearchParams]
  );

  const replaceDraftFromSnapshot = React.useCallback(
    (snapshot: Record<string, unknown>) => {
      const normalizedModel = restoreSpecsModel(
        specType as SpecsEntityType,
        snapshot
      );
      const normalizedSnapshot = normalizedModel.save() as Record<
        string,
        unknown
      >;
      const normalizedSpecName = getSpecsEntityId(
        specType as SpecsEntityType,
        normalizedSnapshot
      );

      setCatalog((current) =>
        current
          ? upsertSpecsSnapshotIntoCatalog(
              current,
              specType as SpecsEntityType,
              normalizedSnapshot
            )
          : current
      );
      hydrateDraftFromSnapshot(normalizedSnapshot, normalizedSpecName);
    },
    [hydrateDraftFromSnapshot, specType]
  );

  const createDraft = React.useCallback(
    ({ specName, displayName }: CreateSpecDraftInput) => {
      if (!catalog) {
        throw new Error(
          `Cannot create ${specTypeLabel} before the catalog loads`
        );
      }

      const trimmedSpecName = specName.trim();
      if (!trimmedSpecName) {
        throw new Error(`Enter a ${specTypeLabel} name`);
      }

      if (
        !isSpecsEntityIdAvailable(
          specType as SpecsEntityType,
          catalog,
          trimmedSpecName
        )
      ) {
        throw new Error(`${specTypeLabel} ${trimmedSpecName} already exists`);
      }

      const snapshot = createDefaultSpecsEntitySnapshot(
        specType as SpecsEntityType,
        {
          specName: trimmedSpecName,
          displayName,
        }
      );
      const normalizedModel = restoreSpecsModel(
        specType as SpecsEntityType,
        snapshot
      );
      const normalizedSnapshot = normalizedModel.save() as Record<
        string,
        unknown
      >;
      const normalizedSpecName = getSpecsEntityId(
        specType as SpecsEntityType,
        normalizedSnapshot
      );

      setCatalog((current) =>
        current
          ? upsertSpecsSnapshotIntoCatalog(
              current,
              specType as SpecsEntityType,
              normalizedSnapshot
            )
          : current
      );
      hydrateDraftFromSnapshot(normalizedSnapshot, normalizedSpecName);
      toast.success(`Created ${specTypeLabel} draft ${normalizedSpecName}`);
    },
    [catalog, hydrateDraftFromSnapshot, specType, specTypeLabel]
  );

  const hydrateSelectedOptionFromCatalog = React.useCallback(() => {
    if (!selectedOption) {
      setDraftJson("");
      setDraftModel(null);
      hydratedDraftSpecNameRef.current = null;
      return;
    }

    const nextModel =
      catalog && selectedOption.id
        ? createSpecsModelFromCatalog(
            specType as SpecsEntityType,
            catalog,
            selectedOption.id
          )
        : null;
    const nextSnapshot = (nextModel?.save() ??
      (selectedOption.entity as Record<string, unknown>)) as Record<
      string,
      unknown
    >;
    setDraftModel(nextModel);
    setDraftJson(formatJson(nextSnapshot));
    hydratedDraftSpecNameRef.current = selectedOption.id;
  }, [catalog, selectedOption, specType]);

  const applyLoadedRevisionSnapshot = React.useCallback(
    (snapshot: Record<string, unknown>, specName: string) => {
      setCatalog((current) =>
        current
          ? upsertSpecsSnapshotIntoCatalog(
              current,
              specType as SpecsEntityType,
              snapshot
            )
          : current
      );
      hydrateDraftFromSnapshot(snapshot, specName);
    },
    [hydrateDraftFromSnapshot, specType]
  );

  const fetchLatestRevisionForSelectedOption = React.useCallback(async () => {
    if (!selectedOption) {
      return null;
    }

    try {
      const revisionsResponse = await SpecsRevisionService.listSpecRevisions(
        specType,
        selectedOption.id,
        { limit: 1 }
      );
      const latestRevisionId = getLatestRevisionId(revisionsResponse);

      if (!latestRevisionId) {
        return null;
      }

      const revisionResponse = await SpecsRevisionService.getSpecRevision(
        specType,
        selectedOption.id,
        latestRevisionId
      );
      const revisionSnapshot = (revisionResponse.data?.json ?? {}) as Record<
        string,
        unknown
      >;
      const normalizedModel = restoreSpecsModel(
        specType as SpecsEntityType,
        revisionSnapshot
      );
      const normalizedSnapshot = normalizedModel.save() as Record<
        string,
        unknown
      >;
      const normalizedSpecName = getSpecsEntityId(
        specType as SpecsEntityType,
        normalizedSnapshot
      );

      return {
        snapshot: normalizedSnapshot,
        specName: normalizedSpecName,
      };
    } catch (error) {
      throw new Error(
        `[SpecsEditor] Failed to load latest saved ${specType} revision for ${selectedOption.id}`,
        {
          cause: error,
        }
      );
    }
  }, [selectedOption, specType]);

  React.useEffect(() => {
    if (!selectedOption) {
      hydrateSelectedOptionFromCatalog();
      return;
    }

    if (hydratedDraftSpecNameRef.current === selectedOption.id) {
      return;
    }

    let cancelled = false;

    const run = async () => {
      const loadedRevision = await fetchLatestRevisionForSelectedOption();

      if (cancelled) {
        return;
      }

      if (!loadedRevision) {
        hydrateSelectedOptionFromCatalog();
        return;
      }

      applyLoadedRevisionSnapshot(
        loadedRevision.snapshot,
        loadedRevision.specName
      );
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [
    applyLoadedRevisionSnapshot,
    fetchLatestRevisionForSelectedOption,
    hydrateSelectedOptionFromCatalog,
    selectedOption,
  ]);

  const reloadSelectedSpec = React.useCallback(() => {
    void (async () => {
      const loadedRevision = await fetchLatestRevisionForSelectedOption();
      if (!loadedRevision) {
        hydrateSelectedOptionFromCatalog();
        return;
      }

      applyLoadedRevisionSnapshot(
        loadedRevision.snapshot,
        loadedRevision.specName
      );
    })();
  }, [
    applyLoadedRevisionSnapshot,
    fetchLatestRevisionForSelectedOption,
    hydrateSelectedOptionFromCatalog,
  ]);

  const parseDraftEntity = React.useCallback(() => {
    try {
      const parsedDraft = parseDraft(draftJson || "");
      const model = restoreSpecsModel(
        specType as SpecsEntityType,
        parsedDraft.entity as Record<string, unknown>
      );
      const snapshot = model.save() as Record<string, unknown>;

      return {
        specName: getSpecsEntityId(specType as SpecsEntityType, snapshot),
        entity: snapshot as Entity,
        model,
      };
    } catch (nextError) {
      modals.showValidationResult({
        valid: false,
        issues: [
          {
            validator: invalidJsonValidator,
            message: invalidJsonMessage,
            details:
              nextError instanceof Error
                ? nextError.message
                : "Invalid JSON payload",
          },
        ],
      } as SpecsEditorValidationResult);
      throw nextError;
    }
  }, [
    draftJson,
    invalidJsonMessage,
    invalidJsonValidator,
    modals,
    parseDraft,
    specType,
  ]);

  const saveDraft = React.useCallback(async () => {
    const { specName, entity } = parseDraftEntity();
    setSaveLoading(true);
    try {
      const response = await SpecsRevisionService.saveSpecRevision({
        specType,
        specName,
        snapshot: entity as Record<string, unknown>,
        createdBy: currentUser?.email || currentUser?.id || "unknown",
        changeNote: saveChangeNote,
      });
      setCatalog((current) =>
        current
          ? upsertSpecsSnapshotIntoCatalog(
              current,
              specType as SpecsEntityType,
              entity as Record<string, unknown>
            )
          : current
      );
      hydrateDraftFromSnapshot(entity as Record<string, unknown>, specName);
      modals.showValidationResult({ valid: true, issues: [] });
      toast.success(
        `Saved revision ${String(response.data?.revisionId ?? "").slice(0, 8)}`
      );
    } catch (nextError) {
      const validationResult = extractSpecsValidationResult(nextError);
      if (validationResult) {
        modals.showValidationResult(
          validationResult as SpecsEditorValidationResult
        );
        toast.error("Validation failed. Fix the issues before saving.");
      } else {
        toast.error(
          nextError instanceof Error
            ? nextError.message
            : `Failed to save ${specType}`
        );
      }
    } finally {
      setSaveLoading(false);
    }
  }, [
    currentUser?.email,
    currentUser?.id,
    hydrateDraftFromSnapshot,
    modals,
    parseDraftEntity,
    saveChangeNote,
    specType,
  ]);

  const saveAndPublish = React.useCallback(async () => {
    if (!catalog) {
      return;
    }

    const { specName, entity, model } = parseDraftEntity();
    setPublishLoading(true);
    try {
      const validationResult = await validateSpecsEntityModel(
        specType as SpecsEntityType,
        catalog,
        model
      );

      modals.showValidationResult(
        validationResult as SpecsEditorValidationResult
      );
      if (!validationResult.valid) {
        return;
      }

      const saveResponse = await SpecsRevisionService.saveSpecRevision({
        specType,
        specName,
        snapshot: entity as Record<string, unknown>,
        createdBy: currentUser?.email || currentUser?.id || "unknown",
        changeNote: publishChangeNote,
      });
      const revisionId = String(saveResponse.data?.revisionId ?? "");
      if (!revisionId) {
        throw new Error(`Missing revision id for published ${specType}`);
      }

      await SpecsRevisionService.publishSpecRevision(
        specType,
        specName,
        revisionId
      );
      setCatalog((current) =>
        current
          ? upsertSpecsSnapshotIntoCatalog(
              current,
              specType as SpecsEntityType,
              entity as Record<string, unknown>
            )
          : current
      );
      hydrateDraftFromSnapshot(entity as Record<string, unknown>, specName);
      modals.showValidationResult({ valid: true, issues: [] });
      toast.success(`Published ${specTypeLabel} ${specName}`);
    } catch (nextError) {
      const validationResult = extractSpecsValidationResult(nextError);
      if (validationResult) {
        modals.showValidationResult(
          validationResult as SpecsEditorValidationResult
        );
        toast.error(`Validation failed. Fix the issues before publishing.`);
      } else {
        toast.error(
          nextError instanceof Error
            ? nextError.message
            : `Failed to publish ${specType}`
        );
      }
    } finally {
      setPublishLoading(false);
    }
  }, [
    catalog,
    currentUser?.email,
    currentUser?.id,
    modals,
    parseDraftEntity,
    publishChangeNote,
    specTypeLabel,
    specType,
    hydrateDraftFromSnapshot,
  ]);

  const loadRevision = React.useCallback(
    async (revisionId: string) => {
      if (!selectedSpecName) {
        return;
      }

      setLoadingRevisionId(revisionId);
      try {
        const response = await SpecsRevisionService.getSpecRevision(
          specType,
          selectedSpecName,
          revisionId
        );
        const revisionSnapshot = (response.data?.json ?? {}) as Record<
          string,
          unknown
        >;
        const model = restoreSpecsModel(
          specType as SpecsEntityType,
          revisionSnapshot
        );
        const normalizedSnapshot = model.save() as Record<string, unknown>;
        const normalizedSpecName = getSpecsEntityId(
          specType as SpecsEntityType,
          normalizedSnapshot
        );

        setCatalog((current) =>
          current
            ? upsertSpecsSnapshotIntoCatalog(
                current,
                specType as SpecsEntityType,
                normalizedSnapshot
              )
            : current
        );
        hydrateDraftFromSnapshot(normalizedSnapshot, normalizedSpecName);
        updateSearchParams({
          spec: normalizedSpecName,
          view: SpecsPageView.Editor,
        });
        toast.success(
          `Loaded revision ${String(revisionId).slice(0, 8)} into the editor`
        );
      } catch (nextError) {
        toast.error(
          nextError instanceof Error
            ? nextError.message
            : `Failed to load ${specType} revision`
        );
      } finally {
        setLoadingRevisionId(null);
      }
    },
    [hydrateDraftFromSnapshot, selectedSpecName, specType, updateSearchParams]
  );

  return {
    catalog,
    loading,
    error,
    draftJson,
    setDraftJson: (nextDraftJson: string) => {
      setDraftJson(nextDraftJson);
      try {
        const parsedDraft = parseDraft(nextDraftJson || "");
        const model = restoreSpecsModel(
          specType as SpecsEntityType,
          parsedDraft.entity as Record<string, unknown>
        );
        setDraftModel(model);
      } catch (error) {
        console.error(
          `[SpecsEditor] Failed to restore ${specType} draft model from edited JSON`,
          error
        );
        // Keep the last valid model while the user is editing invalid JSON.
      }
    },
    draftModel,
    draftModelRevision,
    saveLoading,
    publishLoading,
    loadingRevisionId,
    options,
    selectedSpecName,
    currentView,
    updateSearchParams,
    reloadSelectedSpec,
    createDraft,
    replaceDraftFromSnapshot,
    saveDraft,
    saveAndPublish,
    loadRevision,
  };
}
